/**
 * Interface representing metadata information extracted from `pageId` and `listId`.
 */
export interface Metadata {
  /** The ID of the page. */
  pageId: string

  /** The type of the page. Example: 'product', 'category', etc. */
  pageType: string

  /** The business type of the page. Example: 'b2b', 'b2c', etc. */
  pageBusinessType: string

  /** The personalization type of the page. Example: 'personalized', 'non-personalized', etc. */
  pagePersonalizationType: string

  /** The type of the products list. Example: 'grid', 'carousel', etc. */
  productsListType: string

  /** The category of the products list. Example: 'electronics', 'fashion', etc. */
  productsListCategory: string
}

/**
 * Extracts metadata information from the provided `pageId` and `listId` strings.
 *
 * @param {string} pageId - The unique identifier for the page. Expected format: `<pageType>-<pageBusinessType>-<pagePersonalizationType>`.
 * @param {string} listId - The unique identifier for the products list. Expected format: `<productsListType>-<productsListCategory>`.
 * @returns {Metadata | undefined} An object containing the extracted metadata if valid inputs are provided, or `undefined` if the inputs are invalid.
 *
 * @example
 * const metadata = getMetadata('product-b2c-personalized', 'grid-electronics')
 * console.log(metadata)
 * // Output:
 * // {
 * //   pageId: 'product-b2c-personalized',
 * //   pageType: 'product',
 * //   pageBusinessType: 'b2c',
 * //   pagePersonalizationType: 'personalized',
 * //   productsListType: 'grid',
 * //   productsListCategory: 'electronics'
 * // }
 *
 * @throws Will log errors to the console if either `pageId` or `listId` do not match their expected formats.
 */
export function getMetadata(pageId: string, listId: string): Metadata | undefined {
  const pageIdRegex =
    /^\/?(?<pageType>[a-z-]+)-(?<pageBusinessType>[a-z\d]+)-(?<pagePersonalizationType>[a-z]+)/
  const listIdRegex = /^(?<productsListType>[a-z]+)(?:-(?<productsListCategory>[a-z]+))?$/

  const page = pageId.toLocaleLowerCase().match(pageIdRegex)
  const list = listId.toLocaleLowerCase().match(listIdRegex)

  if (!page) {
    console.error('Invalid PAGE_ID format')
    return
  }

  if (!list?.groups) {
    console.error('Invalid LIST_ID format')
    return
  }

  const { pageType, pageBusinessType, pagePersonalizationType } = page?.groups ?? {}
  const { productsListType, productsListCategory } = list.groups ?? {}

  return {
    pageId,
    pageType,
    pageBusinessType,
    pagePersonalizationType,
    productsListType,
    productsListCategory,
  }
}
